import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
  Settings,
  Delete,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-tabpanel-${index}`}
      aria-labelledby={`ai-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AIKeysManager = ({ open, onClose, onKeysUpdated }) => {
  const { t } = useTranslation();
  const { api } = useAppContext();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [keys, setKeys] = useState({
    gemini_api_key: '',
    openai_api_key: '',
    claude_api_key: '',
    ollama_url: '',
  });
  const [keysStatus, setKeysStatus] = useState({
    gemini: false,
    openai: false,
    claude: false,
    ollama_url: '',
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open) {
      loadKeysStatus();
    }
  }, [open]);

  const loadKeysStatus = async () => {
    try {
      const data = await api.getAIKeysStatus();
      setKeysStatus(data.ai_keys);
      setKeys(prev => ({
        ...prev,
        ollama_url: data.ai_keys.ollama_url || '',
      }));
    } catch (error) {
      console.error('Error loading keys status:', error);
    }
  };

  const handleKeyChange = (provider, value) => {
    setKeys(prev => ({
      ...prev,
      [provider]: value,
    }));
  };

  const toggleShowKey = (provider) => {
    setShowKeys(prev => ({
      ...prev,
      [provider]: !prev[provider],
    }));
  };

  const testApiKey = async (provider) => {
    if (!keys[`${provider}_api_key`] && provider !== 'ollama') return;
    
    setTesting(prev => ({ ...prev, [provider]: true }));
    
    try {
      const data = await api.testAIKey(provider, keys[`${provider}_api_key`]);
      setMessage(data.message);
      
      if (data.valid) {
        setKeysStatus(prev => ({
          ...prev,
          [provider]: true,
        }));
      }
    } catch (error) {
      setMessage('Error testing API key');
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const saveKeys = async () => {
    setLoading(true);
    try {
      // Solo enviar claves que tienen valor
      const keysToUpdate = {};
      
      if (keys.gemini_api_key && keys.gemini_api_key.trim()) {
        keysToUpdate.gemini_api_key = keys.gemini_api_key.trim();
      }
      
      if (keys.openai_api_key && keys.openai_api_key.trim()) {
        keysToUpdate.openai_api_key = keys.openai_api_key.trim();
      }
      
      if (keys.claude_api_key && keys.claude_api_key.trim()) {
        keysToUpdate.claude_api_key = keys.claude_api_key.trim();
      }
      
      // Ollama URL siempre se envía
      keysToUpdate.ollama_url = keys.ollama_url;
      
      const data = await api.updateAIKeys(keysToUpdate);
      setKeysStatus(data.ai_keys);
      setMessage(t('aiKeys.saved'));
      if (onKeysUpdated) onKeysUpdated();
      
      // Clear sensitive data from state
      setKeys({
        gemini_api_key: '',
        openai_api_key: '',
        claude_api_key: '',
        ollama_url: keys.ollama_url,
      });
    } catch (error) {
      setMessage('Error saving API keys');
    } finally {
      setLoading(false);
    }
  };

  const removeKey = async (provider) => {
    // Show confirmation dialog
    const confirmed = window.confirm(t('aiKeys.confirmRemove', { provider }));
    if (!confirmed) return;

    try {
      setLoading(true);
      const data = await api.removeAIKey(provider);
      
      if (data.success) {
        // Update local state
        const keyField = providers.find(p => p.name === provider)?.keyField;
        if (keyField) {
          setKeys(prev => ({ ...prev, [keyField]: '' }));
          setKeysStatus(prev => ({ ...prev, [provider]: false }));
        }
        
        setMessage({
          type: 'success',
          text: t('aiKeys.keyRemoved', { provider })
        });
        if (onKeysUpdated) onKeysUpdated();
      } else {
        throw new Error(data.message || 'Failed to remove key');
      }
    } catch (error) {
      console.error('Error removing API key:', error);
      setMessage({
        type: 'error',
        text: t('aiKeys.removeError', { provider })
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setKeys({
      gemini_api_key: '',
      openai_api_key: '',
      claude_api_key: '',
      ollama_url: keysStatus.ollama_url || 'http://localhost:11434',
    });
    setMessage('');
    setShowKeys({});
    onClose();
  };

  const providers = [
    {
      name: 'gemini',
      label: 'Google Gemini',
      description: t('aiKeys.geminiDesc'),
      keyField: 'gemini_api_key',
      placeholder: 'AIzaSy...',
    },
    {
      name: 'openai',
      label: 'OpenAI',
      description: t('aiKeys.openaiDesc'),
      keyField: 'openai_api_key',
      placeholder: 'sk-...',
    },
    {
      name: 'claude',
      label: 'Anthropic Claude',
      description: t('aiKeys.claudeDesc'),
      keyField: 'claude_api_key',
      placeholder: 'sk-ant-...',
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Settings />
          {t('aiKeys.title')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={t('aiKeys.apiKeys')} />
            <Tab label={t('aiKeys.ollama')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" color="textSecondary" paragraph>
            {t('aiKeys.description')}
          </Typography>

          {message && (
            <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={3}>
            {providers.map((provider) => (
              <Box key={provider.name} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">{provider.label}</Typography>
                  <Chip
                    icon={keysStatus[provider.name] ? <CheckCircle /> : <Error />}
                    label={keysStatus[provider.name] ? t('aiKeys.configured') : t('aiKeys.notConfigured')}
                    color={keysStatus[provider.name] ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="textSecondary" paragraph>
                  {provider.description}
                </Typography>

                <Box display="flex" gap={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    label={t('aiKeys.apiKey')}
                    type={showKeys[provider.keyField] ? 'text' : 'password'}
                    value={keys[provider.keyField]}
                    onChange={(e) => handleKeyChange(provider.keyField, e.target.value)}
                    placeholder={provider.placeholder}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => toggleShowKey(provider.keyField)}
                            edge="end"
                          >
                            {showKeys[provider.keyField] ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => testApiKey(provider.name)}
                    disabled={!keys[provider.keyField] || testing[provider.name]}
                    sx={{ minWidth: 100 }}
                  >
                    {testing[provider.name] ? (
                      <CircularProgress size={20} />
                    ) : (
                      t('aiKeys.test')
                    )}
                  </Button>
                  {keysStatus[provider.name] && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeKey(provider.name)}
                      sx={{ minWidth: 50 }}
                      title={t('aiKeys.remove')}
                    >
                      <Delete />
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="body2" color="textSecondary" paragraph>
            {t('aiKeys.ollamaDesc')}
          </Typography>

          <TextField
            fullWidth
            label={t('aiKeys.ollamaUrl')}
            value={keys.ollama_url}
            onChange={(e) => handleKeyChange('ollama_url', e.target.value)}
            placeholder="http://localhost:11434"
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button 
          onClick={saveKeys} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIKeysManager;
